import { ReactNode, useEffect, useMemo } from 'react';
import { z } from 'zod';

import {
  Box,
  Button,
  Collapse,
  Flex,
  Heading,
  IconButton,
  Table,
  TableCaption,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  VisuallyHidden,
} from '@chakra-ui/react';
import { CheckedIcon, ChevronDownIcon, InfoIcon } from '@udacity/chakra-uds-icons';

import { Markdown } from '~/components/markdown';
import { trackEvent } from '~/features/analytics/track-event';

const plansMatrixRow = z.object({
  title: z.string(),
  description: z.string().optional(),
  plans: z.object({
    individual: z.union([z.string(), z.boolean()]).optional(),
    team: z.union([z.string(), z.boolean()]),
    teamPro: z.union([z.string(), z.boolean()]).optional(),
    enterprise: z.union([z.string(), z.boolean()]),
  }),
});

export const plansMatrixSchema = z.object({
  content: z.array(plansMatrixRow).optional(),
  learnerExperience: z.array(plansMatrixRow).optional(),
  udacityManagementPlatform: z.array(plansMatrixRow).optional(),
  programManagerTools: z.array(plansMatrixRow).optional(),
  solutionsConsultingServices: z.array(plansMatrixRow).optional(),
  integrations: z.array(plansMatrixRow).optional(),
  customerSuccess: z.array(plansMatrixRow).optional(),
  billing: z.array(plansMatrixRow).optional(),
});

const planColumnSchema = z.object({
  id: z.union([z.literal('individual'), z.literal('team'), z.literal('teamPro'), z.literal('enterprise')]),
  title: z.string(),
});

const planColumnsSchema = z.array(planColumnSchema);

type PlansColumns = z.infer<typeof planColumnsSchema>;

export type PlansMatrixProps = z.infer<typeof plansMatrixSchema> & {
  footerContent?: ReactNode;
  label?: string;
  tableColumns?: PlansColumns;
};

const initTableColumns = planColumnsSchema.parse([
  {
    id: 'individual',
    title: 'Individual',
  },
  {
    id: 'team',
    title: 'Team',
  },
  {
    id: 'enterprise',
    title: 'Enterprise',
  },
]);

type PlansMatrixRow = z.infer<typeof plansMatrixRow>;
function PlansMatrixRows({
  title,
  rows,
  columns = initTableColumns,
}: {
  title: string;
  rows: PlansMatrixRow[];
  columns?: PlansColumns;
}) {
  const sharedThickBorderStyles = {
    content: '""',
    borderTop: '0.5px solid',
    color: 'gray.600',
  };
  return (
    <>
      <Tr
        sx={{
          '& > th:last-of-type::before': {
            right: '0px',
          },
        }}
      >
        <Heading
          as='th'
          bgColor='gray.100'
          color='blue.800'
          lineHeight='30px'
          paddingBottom='9px'
          position='relative'
          size='h4'
          textAlign='left'
          whiteSpace='break-spaces'
          sx={{
            '&::before': {
              ...sharedThickBorderStyles,
              position: 'absolute',
              left: 0,
              right: '-20px',
              bottom: 0,
            },
          }}
        >
          {title}
        </Heading>
        {Array.from({ length: columns.length }).map((_, idx) => (
          <Th
            key={idx}
            backgroundColor={bgColor}
            borderRight='none !important'
            position='relative'
            sx={{
              '&::before': {
                ...sharedThickBorderStyles,
                position: 'absolute',
                left: 0,
                right: '-20px',
                bottom: 0,
              },
            }}
          />
        ))}
      </Tr>
      {rows.map((row) => {
        const sharedThinBorderStyles = {
          content: '""',
          borderTop: '0.5px solid',
          borderColor: 'gray.300',
        };

        return (
          <Tr
            key={row.title}
            sx={{
              '& > td:last-of-type::before': {
                right: '0px',
              },
            }}
          >
            <Td
              backgroundColor={bgColor}
              border='none'
              color='blue.800'
              fontWeight='600'
              padding='16px 0px'
              position='relative'
              whiteSpace='normal'
              sx={{
                '&::before': {
                  ...sharedThinBorderStyles,
                  position: 'absolute',
                  left: 0,
                  right: '-20px',
                  bottom: 0,
                },
              }}
            >
              <Flex alignItems='center' gap='12px' justifyContent='space-between'>
                {row.title} {row.description && <Information description={row.description} title={row.title} />}
              </Flex>
            </Td>
            {columns.map((column) => {
              const value = row.plans[column.id];
              return (
                <Td
                  key={column.id}
                  backgroundColor={bgColor}
                  border='none'
                  color='blue.800'
                  position='relative'
                  textAlign='center'
                  whiteSpace='pre-wrap'
                  sx={{
                    '&::before': {
                      ...sharedThinBorderStyles,
                      position: 'absolute',
                      left: 0,
                      right: '-20px',
                      bottom: 0,
                    },
                  }}
                >
                  {typeof value === 'boolean' ? (
                    value === true ? (
                      <CheckedIcon color='black' height={8} width={8} />
                    ) : (
                      '-'
                    )
                  ) : (
                    <Markdown markdownText={value || '-'} />
                  )}
                </Td>
              );
            })}
          </Tr>
        );
      })}
    </>
  );
}

function Information({ title, description }: { title: string; description: string }) {
  const { isOpen, onOpen, onClose, onToggle } = useDisclosure();

  useEffect(() => {
    const keydownHandler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    // Note: we need to close tooltip on scroll otherwise, we have some weird behavior with the tooltip overlapping the main sticky nav.
    const scrollHandler = onClose;

    if (isOpen) {
      window.addEventListener('keydown', keydownHandler);
      window.addEventListener('scroll', scrollHandler);
      return () => {
        window.removeEventListener('keydown', keydownHandler);
        window.removeEventListener('scroll', scrollHandler);
      };
    }
  }, [isOpen, onClose]);

  return (
    <Tooltip isOpen={isOpen} label={description} hasArrow>
      <IconButton
        aria-label={`More information about "${title}".`}
        height='unset'
        icon={<InfoIcon color='black' height={8} width={8} />}
        variant='ghost'
        onBlur={onClose}
        onClick={onToggle}
        onFocus={onOpen}
        onMouseEnter={onOpen}
        onMouseLeave={onClose}
      />
    </Tooltip>
  );
}

const bgColor = 'gray.100';

export function PlansMatrix({
  integrations,
  learnerExperience,
  footerContent = null,
  label = 'See Plan Comparison',
  tableColumns = initTableColumns,
  content,
  udacityManagementPlatform,
  programManagerTools,
  solutionsConsultingServices,
  billing,
  customerSuccess,
}: PlansMatrixProps) {
  const { isOpen, onToggle } = useDisclosure({
    onOpen: () =>
      trackEvent({
        type: 'click',
        name: 'Plans Comparison Clicked',
        label: 'See Plan Comparison',
      }),
  });
  const table = useMemo(() => {
    return (
      <Table
        border='none'
        variant='simple'
        sx={{
          borderSpacing: '16px 0px',
          borderCollapse: 'separate',
          tableLayout: 'fixed',
          bgColor: bgColor,
        }}
      >
        <TableCaption margin={0} padding={0}>
          <VisuallyHidden>Plan Comparisons</VisuallyHidden>
        </TableCaption>
        <Thead>
          <Tr>
            <Th bgColor={bgColor} borderRight='none !important' width='200px'>
              <VisuallyHidden>Features</VisuallyHidden>
            </Th>
            {tableColumns.map((column) => (
              <Th
                key={column.id}
                backgroundColor='white'
                border='1px solid'
                borderColor='accent.sky.500 !important'
                borderRadius='base'
                fontFamily='body'
                fontSize='lg'
                fontWeight='600'
                letterSpacing='normal'
                padding='24px 15px'
                textAlign='center'
                width='195px'
              >
                {column.title}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody
          sx={{
            '& > tr:first-of-type > th:first-of-type': {
              paddingTop: '18px',
            },
            '& > tr:not(:first-of-type) > th:first-of-type': {
              paddingTop: '48px',
            },
          }}
        >
          {content && <PlansMatrixRows columns={tableColumns} rows={content} title='Best in class content' />}
          {learnerExperience && (
            <PlansMatrixRows columns={tableColumns} rows={learnerExperience} title='Learner Experience' />
          )}
          {udacityManagementPlatform && (
            <PlansMatrixRows
              columns={tableColumns}
              rows={udacityManagementPlatform}
              title='Udacity Management Platform'
            />
          )}
          {programManagerTools && (
            <PlansMatrixRows columns={tableColumns} rows={programManagerTools} title='Program Manager Tools' />
          )}
          {solutionsConsultingServices && (
            <PlansMatrixRows
              columns={tableColumns}
              rows={solutionsConsultingServices}
              title='Solutions Consulting Services'
            />
          )}
          {customerSuccess && (
            <PlansMatrixRows columns={tableColumns} rows={customerSuccess} title='Customer Success' />
          )}
          {integrations && <PlansMatrixRows columns={tableColumns} rows={integrations} title='Integrations' />}
          {billing && <PlansMatrixRows columns={tableColumns} rows={billing} title='Billing' />}
        </Tbody>
      </Table>
    );
  }, [
    integrations,
    learnerExperience,
    tableColumns,
    content,
    udacityManagementPlatform,
    programManagerTools,
    solutionsConsultingServices,
    customerSuccess,
    billing,
  ]);
  return (
    <>
      <Button
        alignItems='center'
        display='flex'
        gap='8px'
        margin='0 auto'
        size='icon-right'
        variant='ghost'
        onClick={onToggle}
      >
        {label} <ChevronDownIcon height={5} transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'} width={5} />
      </Button>
      <Collapse in={isOpen}>
        <Box margin='40px auto 0' maxWidth='1148px'>
          <TableContainer position='relative'>{table}</TableContainer>
        </Box>
        <Box height='40px' />
        {footerContent}
      </Collapse>
    </>
  );
}
